import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';
import { TextArea } from '../ui/TextArea';
import { Badge } from '../ui/badge';
import { 
  FeatherPlus,
  FeatherBell,
  FeatherCalendar,
  FeatherUser,
  FeatherCheckCircle,
  FeatherClock,
  FeatherAlertCircle,
  FeatherEdit2,
  FeatherTrash2
} from '../ui/icons';
import { IconButton } from '../ui/IconButton';
import api from '../../services/api';
import './Reminders.css';

const Reminders = ({ companyId, users, onRefresh }) => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    due_date: '',
    assigned_to: ''
  });

  useEffect(() => {
    if (companyId) {
      fetchReminders();
    }
  }, [companyId]);

  const fetchReminders = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/partner-companies/${companyId}`);
              setReminders(response.data.partner_reminders || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch reminders');
      console.error('Error fetching reminders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    console.log('Form field changed:', field, value);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Validate required fields
    if (!formData.title.trim()) {
      setError('Title is required');
      setSubmitting(false);
      return;
    }

    if (!formData.due_date) {
      setError('Due date is required');
      setSubmitting(false);
      return;
    }

    if (!formData.assigned_to) {
      setError('Assigned user is required');
      setSubmitting(false);
      return;
    }

    // Format the data for submission
    const submitData = {
      title: formData.title.trim(),
      message: formData.message?.trim() || '',
      due_date: new Date(formData.due_date).toISOString(),
      assigned_to: formData.assigned_to
    };

    console.log('Submitting reminder data:', submitData);
    console.log('Company ID:', companyId);

    try {
      const response = await api.post(`/api/partner-companies/${companyId}/reminders`, submitData);
      console.log('Reminder created successfully:', response.data);
      
      // Reset form
      setFormData({
        title: '',
        message: '',
        due_date: '',
        assigned_to: ''
      });
      
      setShowForm(false);
      
      // Refresh reminders
      await fetchReminders();
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error creating reminder:', err);
      console.error('Error response:', err.response?.data);
      
      if (err.response?.data?.errors) {
        const errorMessages = err.response.data.errors.map(e => e.msg).join(', ');
        setError(`Validation errors: ${errorMessages}`);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to create reminder');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      'pending': <FeatherClock />,
      'completed': <FeatherCheckCircle />,
      'overdue': <FeatherAlertCircle />
    };
    return icons[status] || <FeatherClock />;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'completed': 'success',
      'overdue': 'danger'
    };
    return colors[status] || 'neutral';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
  };

  const getReminderStatus = (reminder) => {
    if (reminder.status === 'completed') {
      return 'completed';
    } else if (isOverdue(reminder.due_date)) {
      return 'overdue';
    } else {
      return 'pending';
    }
  };

  if (loading) {
    return (
      <div className="reminders">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading reminders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reminders">
      {/* Header */}
      <div className="section-header">
        <div className="header-left">
          <h3>Reminders</h3>
          <Badge variant="neutral-tertiary">{reminders.length} reminders</Badge>
        </div>
        <Button
          variant="brand-primary"
          icon={<FeatherPlus />}
          onClick={() => setShowForm(true)}
        >
          Add Reminder
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <Button
            variant="neutral-tertiary"
            size="sm"
            onClick={() => setError('')}
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Add Reminder Form */}
      {showForm && (
        <div className="add-reminder-form">
          <h4>Add Reminder</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Title</label>
                <TextField.Input
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Reminder title"
                  required
                />
              </div>

              <div className="form-group">
                <label>Due Date</label>
                <input
                  type="datetime-local"
                  value={formData.due_date}
                  onChange={(e) => handleInputChange('due_date', e.target.value)}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label>Assigned To</label>
                <select
                  value={formData.assigned_to}
                  onChange={(e) => handleInputChange('assigned_to', e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Select a user</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.display_name || user.email}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group full-width">
                <label>Message</label>
                <TextArea.Input
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  placeholder="Additional details or notes"
                  rows={3}
                />
              </div>
            </div>

            <div className="form-actions">
              <Button
                type="button"
                variant="neutral-tertiary"
                onClick={() => setShowForm(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="brand-primary"
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create Reminder'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Reminders List */}
      {reminders.length === 0 ? (
        <div className="empty-state">
          <FeatherBell size={48} />
          <h4>No Reminders</h4>
          <p>Create reminders to track follow-ups and important dates</p>
          <Button
            variant="brand-primary"
            icon={<FeatherPlus />}
            onClick={() => setShowForm(true)}
          >
            Add First Reminder
          </Button>
        </div>
      ) : (
        <div className="reminders-list">
          {reminders.map(reminder => {
            const status = getReminderStatus(reminder);
            const assignedUser = users.find(u => u.id === reminder.assigned_to);
            
            return (
              <div key={reminder.id} className={`reminder-item ${status}`}>
                <div className="reminder-header">
                  <div className="reminder-status">
                    <IconButton
                      variant={getStatusColor(status)}
                      size="sm"
                      icon={getStatusIcon(status)}
                    />
                    <Badge variant={getStatusColor(status)}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Badge>
                  </div>
                  
                  <div className="reminder-actions">
                    <IconButton
                      variant="neutral-tertiary"
                      size="sm"
                      icon={<FeatherEdit2 />}
                      title="Edit Reminder"
                    />
                    <IconButton
                      variant="neutral-tertiary"
                      size="sm"
                      icon={<FeatherTrash2 />}
                      title="Delete Reminder"
                    />
                  </div>
                </div>

                <div className="reminder-content">
                  <h4 className="reminder-title">{reminder.title}</h4>
                  
                  {reminder.message && (
                    <p className="reminder-message">{reminder.message}</p>
                  )}
                </div>

                <div className="reminder-meta">
                  <div className="reminder-due">
                    <FeatherCalendar size={14} />
                    <span>Due: {formatDate(reminder.due_date)}</span>
                  </div>
                  
                  {assignedUser && (
                    <div className="reminder-assigned">
                      <FeatherUser size={14} />
                      <span>Assigned to: {assignedUser.display_name || assignedUser.email}</span>
                    </div>
                  )}
                </div>

                {status === 'overdue' && (
                  <div className="overdue-warning">
                    <FeatherAlertCircle size={14} />
                    <span>This reminder is overdue</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Reminders; 